/**
 * Inquiry Service
 *
 * API calls for inquiry management
 */

import { api } from '../lib/api';

export type InquiryType = 'SAMPLE_REQUEST' | 'BULK_ORDER';
export type InquiryStatus =
  | 'PENDING'
  | 'UNDER_REVIEW'
  | 'QUOTE_SENT'
  | 'QUOTE_ACCEPTED'
  | 'QUOTE_DECLINED'
  | 'QUOTE_REVISION_REQUESTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED';
export type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';

export interface InquiryListItem {
  id: string;
  referenceNo: string;
  type: InquiryType;
  status: InquiryStatus;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
  hasQuote: boolean;
  user: {
    id: string;
    name: string | null;
    email: string;
    companyName: string | null;
  };
}

export interface InquiryItem {
  id: string;
  quantity: number;
  color: string | null;
  notes: string | null;
  product: {
    id: string;
    name: string;
    slug: string;
    sku: string;
    primaryImage: string | null;
  };
}

export interface QuoteItem {
  id: string;
  quantity: number;
  unitPrice: number;
  total: number;
  product: {
    id: string;
    name: string;
    sku: string;
  };
}

export interface Quote {
  id: string;
  totalAmount: number;
  validityDate: string;
  status: QuoteStatus;
  pdfUrl: string | null;
  items: QuoteItem[];
}

export interface Message {
  id: string;
  senderType: 'USER' | 'ADMIN';
  message: string;
  createdAt: string;
}

export interface InquiryDetails {
  id: string;
  referenceNo: string;
  type: InquiryType;
  status: InquiryStatus;
  deliveryAddress: string | null;
  logoDescription: string | null;
  timeline: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string | null;
    email: string;
    companyName: string | null;
  };
  items: InquiryItem[];
  quote: Quote | null;
  messages: Message[];
}

export interface CreateQuoteInput {
  items: Array<{
    productId: string;
    quantity: number;
    unitPrice: number;
  }>;
  validityDays?: number;
}

export interface InquiryListOptions {
  page?: number;
  limit?: number;
  type?: InquiryType;
  status?: InquiryStatus;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export const inquiryService = {
  list: async (options: InquiryListOptions = {}): Promise<PaginatedResponse<InquiryListItem>> => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.type) params.append('type', options.type);
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const response = await api.get<InquiryListItem[]>(`/admin/inquiries?${params.toString()}`);
    return {
      data: response.data || [],
      meta: response.meta || { page: 1, limit: 20, total: 0, hasMore: false },
    };
  },

  getById: async (id: string): Promise<InquiryDetails> => {
    const response = await api.get<InquiryDetails>(`/admin/inquiries/${id}`);
    return response.data!;
  },

  updateStatus: async (id: string, status: InquiryStatus): Promise<InquiryDetails> => {
    const response = await api.patch<InquiryDetails>(`/admin/inquiries/${id}/status`, { status });
    return response.data!;
  },

  addMessage: async (id: string, message: string): Promise<Message> => {
    const response = await api.post<Message>(`/admin/inquiries/${id}/messages`, { message });
    return response.data!;
  },

  createQuote: async (id: string, data: CreateQuoteInput): Promise<InquiryDetails> => {
    const response = await api.post<InquiryDetails>(`/admin/inquiries/${id}/quote`, data);
    return response.data!;
  },

  sendQuote: async (id: string): Promise<InquiryDetails> => {
    const response = await api.post<InquiryDetails>(`/admin/inquiries/${id}/quote/send`);
    return response.data!;
  },
};
